import type { JSX, ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import type { TranslationKeys } from '../../../i18n/types.js';

export interface PeekSheetData {
  readonly name: string;
  readonly portraitUrl: string | undefined;
  readonly ac: number | undefined;
  readonly hp: number | undefined;
  readonly speed: number | undefined;
}

export interface PeekCardProps {
  readonly sheet: PeekSheetData;
  readonly t: (key: keyof TranslationKeys) => string;
  readonly children: ReactNode;
  readonly openDelayMs?: number;
}

const DEFAULT_OPEN_DELAY_MS = 150;

interface PeekStatProps {
  readonly label: string;
  readonly value: number | undefined;
}

function PeekStat({ label, value }: PeekStatProps): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-label font-semibold uppercase tracking-label text-dim">
        {label}
      </span>
      <span className="text-ui font-semibold tabular-nums">
        {value === undefined ? '\u2014' : value}
      </span>
    </div>
  );
}

export function PeekCard({
  sheet,
  t,
  children,
  openDelayMs = DEFAULT_OPEN_DELAY_MS,
}: PeekCardProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clearTimer = useCallback((): void => {
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  const scheduleOpen = useCallback(
    (next: boolean): void => {
      clearTimer();
      timerRef.current = setTimeout((): void => {
        setOpen(next);
      }, openDelayMs);
    },
    [clearTimer, openDelayMs],
  );

  const handleMouseEnter = useCallback((): void => {
    scheduleOpen(true);
  }, [scheduleOpen]);

  const handleMouseLeave = useCallback((): void => {
    scheduleOpen(false);
  }, [scheduleOpen]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        asChild
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span className="inline-flex">{children}</span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="center"
          sideOffset={8}
          onOpenAutoFocus={(event: Event): void => {
            event.preventDefault();
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className="z-50 w-56 rounded-md border border-border bg-popover p-3 text-popover-foreground shadow-md outline-none"
        >
          <div className="flex items-center gap-3">
            {sheet.portraitUrl === undefined ? (
              <div aria-hidden="true" className="h-12 w-12 shrink-0 rounded-md bg-muted" />
            ) : (
              <img
                src={sheet.portraitUrl}
                alt={sheet.name}
                className="h-12 w-12 shrink-0 rounded-md object-cover"
              />
            )}
            <span className="truncate text-ui font-semibold">{sheet.name}</span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <PeekStat label={t('studio.peek.ac')} value={sheet.ac} />
            <PeekStat label={t('studio.peek.hp')} value={sheet.hp} />
            <PeekStat label={t('studio.peek.speed')} value={sheet.speed} />
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
